import { useContext } from 'react';
import { closestCenter, DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { QuestionBox } from '../QuestionBox';
import { LangContext } from '@/context/LangProvider';
import {
  DiscussionGuideFormProps,
  DiscussionGuideProjectProps,
  DiscussionGuideQuestionProps,
} from './types';

interface FlatModeViewProps {
  // Core structure
  structure: {
    isRootRandomized: boolean;
    hasQuestions: boolean;
    isDisabled: boolean;
    isUserViewer: boolean;
  };
  // Drag & drop
  dragAndDrop: {
    onDragEnd: (event: DragEndEvent) => void;
    onDragStart: (event: DragStartEvent) => void;
  };
  // Handlers
  handlers: {
    onDeleteQuestion: (idToDelete: string) => void;
    isQuestionInDatabase: (questionId: string) => boolean;
  };
  // Form props
  formProps: DiscussionGuideFormProps;
  // Project props
  projectProps: DiscussionGuideProjectProps;
  // Question props
  questionProps: DiscussionGuideQuestionProps;
}

export function FlatModeView({
  structure,
  dragAndDrop,
  handlers,
  formProps,
  projectProps,
  questionProps,
}: FlatModeViewProps) {
  const { isRootRandomized, hasQuestions, isDisabled, isUserViewer } = structure;
  const { onDragEnd, onDragStart } = dragAndDrop;
  const { onDeleteQuestion, isQuestionInDatabase } = handlers;
  const { register, control, setValue, clearErrors, errors } = formProps;
  const { isProjectPublished, isProjectCompleted, singleProjectData } = projectProps;
  const { questions, openQuestionId, setOpenQuestionId, setFileData, fileData, currentQuestions } =
    questionProps;
  const { lang } = useContext(LangContext);

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <div
        className={`flex flex-col w-full h-full gap-5 p-6 rounded-md border ${isRootRandomized && hasQuestions ? 'bg-green-50 border-green-500' : 'bg-slate-50 border-transparent'}`}
      >
        {!hasQuestions && (
          <p className="text-sm font-medium">{lang.get('msg.questionsFieldEmpty')}</p>
        )}
        <SortableContext
          disabled={isDisabled}
          items={questions}
          strategy={verticalListSortingStrategy}
        >
          {questions.map((question, index) => {
            // The validation schema uses a root group structure, so errors are nested:
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const interviewQuestionsError = errors?.interview_questions as any;
            const questionError = interviewQuestionsError?.children?.[index]?.question;

            const registrationPath = `interview_questions.children[${index}]`;

            return (
              <QuestionBox
                isProjectPublished={isProjectPublished}
                isProjectCompleted={isProjectCompleted}
                isActiveDrag={false}
                key={question.id}
                treePath={[index]}
                registrationPath={registrationPath}
                displayNumber={index + 1}
                register={register}
                id={question.id}
                control={control}
                handleDeleteQuestion={onDeleteQuestion}
                isUserViewer={isUserViewer}
                existingText={question.question}
                singleProjectData={singleProjectData}
                questions={questions}
                openQuestionId={openQuestionId}
                setOpenQuestionId={setOpenQuestionId}
                setFileData={setFileData}
                fileData={fileData}
                currentQuestion={currentQuestions?.[index]}
                setValue={setValue}
                clearErrors={clearErrors}
                error={questionError}
                isQuestionSaved={isQuestionInDatabase(question.id)}
              />
            );
          })}
        </SortableContext>
      </div>
    </DndContext>
  );
}
